import './sentry'
import './intercom'
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { TamaguiProvider } from 'tamagui'
import '@tamagui/core/reset.css'
import 'stream-chat-react/dist/css/v2/index.css'
import './styles/global.css'
import './styles/stream-overrides.css'
import { tamaguiConfig } from '@cobu/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import AppShell from './AppShell'
import { loadTenant } from './lib/tenant/loadTenant'
import { detectTenantFromPath, getTenantBasePath } from './lib/tenant/detectTenant'
import { SessionProvider } from './lib/session/SessionProvider'
import { registerSW } from 'virtual:pwa-register'
import { Provider } from 'react-redux'
import { store } from './store'
import { setStreamApiKey } from '@cobu/stream-chat'
import { analyticsService } from '@cobu/analytics'
import { setTokenResolver } from '@cobu/api-client'
import { logger } from './utils/logger'
import { Strings } from './constants/Strings'
import { setLanguage as setReduxLanguage } from './store/authSlice'
import { setDateLocale } from './utils/dateTime'

// Configure API client to use token from Redux store
setTokenResolver(() => {
  const state = store.getState()
  return state.auth.token
})

const queryClient = new QueryClient()

const updateSW = registerSW({
  onNeedRefresh() {
    const ok = confirm(Strings.get().pwa.updatePrompt)
    if (ok) updateSW(true)
  },
})

loadTenant().catch(() => {})

// Initialize Stream Chat API key (web)
try {
  if (import.meta.env.VITE_STREAM_TOKEN) {
    setStreamApiKey(import.meta.env.VITE_STREAM_TOKEN)
  }
} catch {}
// Initialize analytics once on startup
analyticsService.initialize({
  mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
  superProperties: { data_source: 'MP-Web', platform: 'web' },
  enableInDev: false, // Enable analytics in development mode
})

// Detect tenant from URL path for dynamic routing
const detectedTenant = detectTenantFromPath()
const basename = getTenantBasePath(detectedTenant)

// Log detected tenant for debugging (remove in production if needed)
if (detectedTenant) {
  logger.log('🏢 Detected tenant:', detectedTenant, '| Base path:', basename)
}

// Resolve initial language synchronously from URL (?language=es) or localStorage.
// English is the static default and incurs zero async work; Spanish triggers a
// dynamic import of the Spanish dictionary chunk which we await before render
// to avoid a flash of English content.
const initialLanguage = Strings.resolveInitialLanguage()
setDateLocale(initialLanguage)

const renderApp = () => {
  // Mirror the resolved language into Redux so any subscribers (e.g.
  // server-side analytics, Accept-Language headers, GetStream client) see it.
  store.dispatch(setReduxLanguage(initialLanguage))

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>{Strings.get().pwa.errorOccurred}</p>}>
        <TamaguiProvider config={tamaguiConfig} defaultTheme="entrata">
          <QueryClientProvider client={queryClient}>
            <BrowserRouter basename={basename}>
              <Provider store={store}>
                <SessionProvider>
                  <AppShell />
                  <Toaster position="top-center" />
                </SessionProvider>
              </Provider>
            </BrowserRouter>
          </QueryClientProvider>
        </TamaguiProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  )
}

if (initialLanguage === 'en') {
  // Fast path: English is statically bundled. Render immediately.
  renderApp()
} else {
  // Spanish (or any future non-default language): await the dictionary chunk
  // before the first paint so we don't flash English copy at the user.
  Strings.setLanguage(initialLanguage)
    .catch((err) => {
      logger.error('[i18n] Failed to load language bundle, falling back to English:', err)
    })
    .finally(renderApp)
}
